import { useRouter } from 'next/navigation';
import React, { type JSX } from 'react';
import ReactDOM from 'react-dom';

import { getUiConfig } from '@/config/client/getUiConfig';
import { signIn } from '@/features/auth/signin';
import { useErrorStore } from '@/stores/errorStore';
import { goBack } from '@/utils/navigation/navigate';

import { type ErrorAction, type ErrorCode, errorConfig } from './errorConfigs';

function HelpLink(): JSX.Element {
  const uiConfig = getUiConfig();

  return (
    <div className="py-1 text-center font-FoxOneAero text-[12px] font-medium leading-[16.8px] text-[rgba(255,255,255,0.25)] sm:py-4">
      <a
        href={uiConfig.helpUrl}
        className="mx-2 cursor-pointer text-[13px] font-normal leading-[18px] text-[rgba(255,255,255,0.70)]"
      >
        Need Help?
      </a>
    </div>
  );
}

interface ErrorOverlayProps {
  code: ErrorCode;
  targetId?: string;
}

export function ErrorOverlay({ code, targetId }: ErrorOverlayProps): React.ReactNode {
  const uiConfig = getUiConfig();
  const config = errorConfig[code];
  const router = useRouter();

  const clearErrors = useErrorStore((state) => state.clearErrors);

  let targetElement: HTMLElement = document.body;

  if (targetId) {
    targetElement = document.getElementById(targetId) ?? document.body;
  } else if (config.scope === 'player') {
    targetElement = document.getElementById('player-id') ?? document.body;
  }

  const isPlayerScoped = targetElement !== document.body;
  const overlayClass = isPlayerScoped
    ? 'absolute inset-0 z-[999] flex items-center justify-center bg-base-black'
    : 'fixed inset-0 z-[999] flex items-center justify-center bg-base-black';

  function handleErrorAction(action: ErrorAction): void {
    clearErrors();

    switch (action) {
      case 'reload':
        window.location.reload();
        break;
      case 'back':
        if (isPlayerScoped) {
          goBack(router);
        }

        break;
      case 'back-non-player':
        goBack(router);
        break;
      case 'home':
        window.location.href = '/home';
        break;
      case 'signIn':
        void signIn();
        break;
      case 'help':
        window.location.href = uiConfig.helpUrl;
        break;
      case 'manageSub':
        window.location.href = uiConfig.manageSubscriptionUrl;
        break;
      case 'subscribeUrl':
        window.location.href = uiConfig.subscribeUrl;
        break;

      default:
    }
  }

  const overlay = (
    <div aria-modal="true" role="dialog" className={overlayClass} aria-labelledby="dialog-title">
      <div className="mx-2 flex size-full min-h-[180px] max-w-[936px] flex-col items-center justify-center overflow-y-auto sm:mx-4 sm:min-h-[361px]">
        <h2
          id="dialog-title"
          className="mb-3 text-center font-FoxOneAero text-[20px] font-medium leading-[1.2] text-white sm:text-[30px]"
        >
          {config.title}
        </h2>
        <div className="mb-2 text-center font-FoxOneAero text-[15px] font-normal leading-tight text-[#D8D8D8] sm:mb-11 sm:text-[19px]">
          {config.message}
        </div>
        <button
          className="mb-2 flex h-10 w-[180px] items-center justify-center gap-1 rounded-full bg-[#F1F1F1] font-FoxOneAero text-[16px] font-extrabold uppercase leading-none text-black sm:mb-7 sm:h-12 sm:w-[294px] sm:text-[20px]"
          onClick={() => {
            handleErrorAction(config.positiveCtaAction);
          }}
        >
          {config.positiveCta}
        </button>
        {config.negativeCta !== false && (
          <button
            className="mb-1 flex h-10 w-[180px] items-center justify-center gap-1 rounded-full border border-white bg-base-black font-FoxOneAero text-[16px] font-extrabold uppercase leading-none text-white sm:mb-12 sm:h-12 sm:w-[294px] sm:text-[20px]"
            onClick={() => {
              handleErrorAction(config.negativeCtaAction);
            }}
          >
            {config.negativeCta}
          </button>
        )}
        {config.linkSierraCta ? <HelpLink /> : null}
      </div>
    </div>
  );

  return ReactDOM.createPortal(overlay, targetElement);
}
